/**
 * @file api.ts
 * @brief React Query hooks for platform-wide (super-admin) MeetingIntel.
 *
 * @details
 * Talks to the dedicated super-admin boardroom API (`/api/admin/boardroom/*`),
 * which returns **every** meeting on the platform with owner + company
 * attribution. Read hooks: list (server-side pagination/filtering incl.
 * owner_id / company_id) + detail + vendor cost. Manage mutations mirror the
 * admin write endpoints (reprocess-diarization, regenerate-intelligence,
 * delete). Metadata only (B2/W4, DES 22-10-2026): VERBLIZR staff have no
 * resolve, translate or export (those routes answer 403 STAFF_NO_CONTENT).
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client (apiGet/apiPost/apiDelete)
 * - shared/api/endpoints (adminEndpoints.boardroom*)
 *
 * @references
 * - BACKEND/src/features/boardroom/admin.routes.js
 * - features/admin-meetings/schemas.ts
 * - features/company-meetings/api.ts (sibling pattern)
 *
 * @author DES
 * @date 08-07-2026
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@/shared/api/client";
import { z } from "zod";
import { adminEndpoints } from "@/shared/api/endpoints";
import {
  AdminMeetingCogsSchema,
  AdminMeetingDetailResponseSchema,
  AdminMeetingsListResponseSchema,
  type AdminMeetingCogs,
  type AdminMeetingDetail,
  type AdminMeetingsListParams,
  type AdminMeetingsListResponse,
} from "./schemas";

// ----------------------------------------------------------------------
// Query keys
// ----------------------------------------------------------------------
export const adminMeetingsKeys = {
  all: ["admin", "meetings"] as const,
  list: (params: AdminMeetingsListParams) =>
    ["admin", "meetings", "list", params] as const,
  detail: (id: string) => ["admin", "meetings", "detail", id] as const,
  cogs: (id: string) => ["admin", "meetings", "cogs", id] as const,
};

// ----------------------------------------------------------------------
// Query string builder — page/pageSize/search/from/to + owner_id/company_id
// ----------------------------------------------------------------------
function buildQuery(params: AdminMeetingsListParams): string {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const usp = new URLSearchParams();
  usp.set("page", String(page));
  usp.set("pageSize", String(pageSize));
  if (params.search) usp.set("search", params.search);
  if (params.from) usp.set("from", params.from);
  if (params.to) usp.set("to", params.to);
  if (params.owner_id) usp.set("owner_id", params.owner_id);
  if (params.company_id) usp.set("company_id", params.company_id);
  return usp.toString();
}

async function fetchAdminMeetingsPage(
  params: AdminMeetingsListParams,
): Promise<AdminMeetingsListResponse> {
  const raw = await apiGet<unknown>(
    `${adminEndpoints.boardroomSessions}?${buildQuery(params)}`,
  );
  return AdminMeetingsListResponseSchema.parse(raw);
}

export function useAdminMeetingsList(
  params: AdminMeetingsListParams,
  options?: Partial<UseQueryOptions<AdminMeetingsListResponse>>,
) {
  return useQuery<AdminMeetingsListResponse>({
    queryKey: adminMeetingsKeys.list(params),
    queryFn: () => fetchAdminMeetingsPage(params),
    placeholderData: (prev) => prev,
    ...options,
  });
}

// ----------------------------------------------------------------------
// Server-backed company -> user groups (DES 01-10-2026: "haan superadmin
// pages pe bhi yehi kar do"). Replaces fetching every page and grouping on
// the client: the server returns (company, owner) pairs with counts and each
// open user group fetches its own meetings.
// ----------------------------------------------------------------------

/** The list filters without paging (shared by the grouped calls). */
function filterQuery(params: AdminMeetingsListParams): URLSearchParams {
  const usp = new URLSearchParams(buildQuery(params));
  usp.delete("page");
  usp.delete("pageSize");
  return usp;
}

/** One (company, owner) pair from `?group=company_owner`. */
export const MeetingsCompanyOwnerSummarySchema = z.object({
  company_id: z.string().nullable(),
  company_name: z.string().nullable(),
  owner_id: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  avatar_url: z.string().nullable(),
  count: z.number(),
});
export type MeetingsCompanyOwnerSummary = z.infer<typeof MeetingsCompanyOwnerSummarySchema>;

/** Group keys: a company (`none` = Personal) and its owner leaf. */
export const meetingCompanyGroupKey = (companyId: string | null) => companyId ?? "none";
export const meetingCompanyOwnerGroupKey = (companyId: string | null, ownerId: string | null) =>
  `${meetingCompanyGroupKey(companyId)}/${ownerId ?? "none"}`;

/** Every (company, owner) pair with meetings under the current filters. */
export function useAdminMeetingsGroupSummary(params: AdminMeetingsListParams, enabled: boolean) {
  return useQuery({
    queryKey: [...adminMeetingsKeys.all, "groups", filterQuery(params).toString()],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const usp = filterQuery(params);
      usp.set("group", "company_owner");
      const raw = await apiGet<{ groups?: unknown }>(
        `${adminEndpoints.boardroomSessions}?${usp}`,
      );
      return z.array(MeetingsCompanyOwnerSummarySchema).parse(raw.groups ?? []);
    },
  });
}

/** Rows per batch when a group opens or loads more. */
export const ADMIN_MEETING_GROUP_BATCH = 25;

/**
 * The meetings of each OPEN leaf group (`company/owner` key), `batches`
 * pages of ADMIN_MEETING_GROUP_BATCH under the same filters
 * (`?company=&owner=`). The route caps `pageSize` at 100, so pages are
 * fetched and joined.
 */
export function useAdminMeetingsGroupRows(
  params: AdminMeetingsListParams,
  open: { key: string; batches: number }[],
) {
  return useQueries({
    queries: open.map(({ key, batches }) => ({
      queryKey: [...adminMeetingsKeys.all, "group", key, batches, filterQuery(params).toString()],
      placeholderData: (prev: AdminMeetingsListResponse | undefined) => prev,
      queryFn: async (): Promise<AdminMeetingsListResponse> => {
        const [company, owner] = key.split("/");
        const pages = await Promise.all(
          Array.from({ length: batches }, async (_, i) => {
            const usp = filterQuery(params);
            usp.set("company", company ?? "none");
            usp.set("owner", owner ?? "none");
            usp.set("page", String(i + 1));
            usp.set("pageSize", String(ADMIN_MEETING_GROUP_BATCH));
            const raw = await apiGet<unknown>(`${adminEndpoints.boardroomSessions}?${usp}`);
            return AdminMeetingsListResponseSchema.parse(raw);
          }),
        );
        return { ...pages[0]!, meetings: pages.flatMap((p) => p.meetings ?? []) };
      },
    })),
  });
}

// ----------------------------------------------------------------------
// Detail — GET /api/admin/boardroom/sessions/:id
// ----------------------------------------------------------------------
export function useAdminMeetingDetail(id: string | null, enabled = true) {
  return useQuery<AdminMeetingDetail>({
    queryKey: adminMeetingsKeys.detail(id ?? ""),
    enabled: enabled && Boolean(id),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.boardroomSessionById(id!));
      return AdminMeetingDetailResponseSchema.parse(raw).meeting;
    },
    // Poll while diarization/synthesis runs (background jobs) so the detail
    // refreshes to the fresh result without a manual reload. (08-07-2026)
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "processing" || s === "synthesis_processing" ? 3000 : false;
    },
  });
}

// ----------------------------------------------------------------------
// Vendor cost — GET /api/admin/boardroom/sessions/:id/cogs (super-admin only)
// ----------------------------------------------------------------------
export function useAdminMeetingCogs(id: string | null) {
  return useQuery<AdminMeetingCogs>({
    queryKey: adminMeetingsKeys.cogs(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () =>
      AdminMeetingCogsSchema.parse(await apiGet<unknown>(adminEndpoints.boardroomCogs(id!))),
  });
}

// ----------------------------------------------------------------------
// Pagination meta derivation
// ----------------------------------------------------------------------
export function adminMeetingsPagination(
  data: AdminMeetingsListResponse | undefined,
  params: AdminMeetingsListParams,
) {
  const pageSize = params.pageSize ?? 20;
  const page = params.page ?? 1;
  const total = Number(data?.total ?? 0);
  return {
    page,
    limit: pageSize,
    total,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
  };
}

// ----------------------------------------------------------------------
// Manage mutations (platform-wide authority)
// ----------------------------------------------------------------------
export function useDeleteAdminMeeting() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (id) =>
      apiDelete<unknown>(adminEndpoints.boardroomDelete(id)),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.detail(id) });
    },
  });
}

export function useReprocessAdminMeetingDiarization() {
  const qc = useQueryClient();
  return useMutation<AdminMeetingDetail, Error, string>({
    mutationFn: async (id) => {
      const raw = await apiPost<unknown>(
        adminEndpoints.boardroomReprocessDiarization(id),
        {},
      );
      return AdminMeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.detail(id) });
    },
  });
}

export type RegenerateIntelligenceVars = {
  id: string;
  force?: boolean;
};

export function useRegenerateAdminMeetingIntelligence() {
  const qc = useQueryClient();
  return useMutation<AdminMeetingDetail, Error, RegenerateIntelligenceVars>({
    mutationFn: async ({ id, force = false }) => {
      const raw = await apiPost<unknown>(
        adminEndpoints.boardroomRegenerateIntelligence(id),
        { force },
      );
      return AdminMeetingDetailResponseSchema.parse(raw).meeting;
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.detail(id) });
    },
  });
}
